import {useEffect, useMemo, useReducer} from 'react';

import {
  buildLogTimestampFormatters,
  hostTimeZone,
  type LogTimestampFormatters,
} from '../logTimestamp';

/**
 * useLogTimestampFormatters returns the Intl formatters for rendering log
 * timestamps in the host time zone, rebuilding them when the zone changes.
 */
export function useLogTimestampFormatters(): LogTimestampFormatters {
  // TODO(react-compiler): reads hostTimeZone() during render; compiled output
  // would cache it.
  'use no memo';
  const [, recheckZone] = useReducer((count: number) => count + 1, 0);

  // No browser event fires on a time-zone change, so also re-check when the
  // page is reactivated after being inactive; this catches an idle viewer
  // that has stopped re-rendering.
  useEffect(() => {
    window.addEventListener('focus', recheckZone);
    document.addEventListener('visibilitychange', recheckZone);
    window.addEventListener('pageshow', recheckZone);
    return () => {
      window.removeEventListener('focus', recheckZone);
      document.removeEventListener('visibilitychange', recheckZone);
      window.removeEventListener('pageshow', recheckZone);
    };
  }, []);

  // Re-read on every render so an actively polling viewer converges within a
  // poll tick; the memo rebuilds only when the zone actually changes.
  const zone = hostTimeZone();
  return useMemo(() => buildLogTimestampFormatters(zone), [zone]);
}
